'use client'

import { useAuthorization } from '@entities/authentication'
import type { PrepareKrPayment } from '@entities/kr-payment/model'
import { usePaymentErrorStaticModel } from '@entities/payment'
import { paymentMethodsEntityMutations } from '@entities/payment-methods'
import { getCustomerUid, HandlePaymentQueue, PaymentCustomData, usePaymentByCardParking } from '@features/payment'
import { useRegisterCardFormState } from '@features/payment-methods'
import { rpc } from '@server/infrastructure/rpc'
import { PaymentMessageBodyType } from '@server/schemas'
import { error, success } from '@shared/libs'
import { openAlertDialog } from '@shared/ui'
import { useMutation } from '@tanstack/react-query'

type UsePaymentByCreditCardProps = {
  handlePaymentQueue: HandlePaymentQueue
  validatePayment: () => boolean
  captureTrialContext?: boolean
  preparePayment?: PrepareKrPayment
  validateClientSideRegisterCard: () => boolean
  validateServerSideRegisterCard: (error: Error) => void
  customerUid: Nullable<string>
  merchantName: string
  amount: number
  currency?: string
  customData: PaymentCustomData
  checkCouponIfUseCoupon?: () => Promise<void>
  paymentType: Extract<PaymentMessageBodyType, 'FIRST_BILLING' | 'TRIAL'>
  billingFailSlackMessage: (errorCode?: string, errorMessage?: string) => string
  paymentWebhookUrl: URL
}

export const usePaymentByCreditCard = ({
  handlePaymentQueue,
  validatePayment,
  preparePayment,
  captureTrialContext,
  validateClientSideRegisterCard,
  validateServerSideRegisterCard,
  customerUid,
  merchantName,
  amount,
  currency = 'KRW',
  customData,
  checkCouponIfUseCoupon,
  paymentType,
  billingFailSlackMessage,
  paymentWebhookUrl,
}: UsePaymentByCreditCardProps) => {
  const authorization = useAuthorization()
  const { ERROR_MESSAGES } = usePaymentErrorStaticModel()
  const registerCard = useMutation(paymentMethodsEntityMutations.registerCard(authorization))

  const { formState: registerCardFormState } = useRegisterCardFormState()

  const paymentByCardParking = usePaymentByCardParking({
    captureTrialContext,
    preparePayment,
    handlePaymentQueue,
    failSlackWebHook: async (errorCode, errorMessage) => {
      openAlertDialog({ title: `${errorMessage}` })

      // Slack 실패 로깅은 사용자 흐름을 막지 않도록 await 하지 않는다
      void rpc.api.v1.payment['logging-payment-success']
        .$post(
          {
            json: {
              action: paymentType === 'TRIAL' ? 'SLACK_TRIAL_FAIL' : 'SLACK_FAIL',
              message: billingFailSlackMessage(errorCode, errorMessage),
            },
          },
          {
            headers: {
              Authorization: `Bearer ${authorization.bearerToken}`,
            },
          },
        )
        .catch(() => {})
    },
  })

  const registerCardDto = {
    cardNumber: `${registerCardFormState.values.cardNumber1}${registerCardFormState.values.cardNumber2}${registerCardFormState.values.cardNumber3}${registerCardFormState.values.cardNumber4}`,
    cardExp: `20${registerCardFormState.values.cardExpiryYear}${registerCardFormState.values.cardExpiryMonth}`,
    cardPassword: registerCardFormState.values.cardPassword,
    cardBirthDate: registerCardFormState.values.birthDate,
  }

  const registerCardProcess = async () => {
    const isValidRegisterCardFormState = validateClientSideRegisterCard()
    if (!isValidRegisterCardFormState) {
      throw new Error(ERROR_MESSAGES.CARD_REGISTRATION_FAILED)
    }

    const { data: customerUid } = await registerCard.mutateAsync(
      { ...registerCardDto, vendor: 'CARD' },
      {
        onError: validateServerSideRegisterCard,
      },
    )

    return customerUid
  }

  const handlePaymentForNewCreditCard = async (): Promise<'success' | 'error'> => {
    if (!validatePayment()) {
      throw new Error(`${ERROR_MESSAGES.PAYMENT_FAILED} handlePaymentForNewCreditCard`)
    }

    try {
      await checkCouponIfUseCoupon?.()

      const customerUidByCardParking = await registerCardProcess()

      await paymentByCardParking({
        customerUid: getCustomerUid(customerUidByCardParking),
        merchantName,
        amount,
        currency,
        customData,
        noticeUrl: paymentWebhookUrl.toString(),
      })

      return success()
    } catch {
      return error()
    }
  }
  const handlePaymentForRegisteredCreditCard = async (): Promise<'success' | 'error'> => {
    if (!validatePayment()) {
      throw new Error(`${ERROR_MESSAGES.PAYMENT_FAILED} handlePaymentForRegisteredCreditCard`)
    }

    try {
      await paymentByCardParking({
        customerUid: getCustomerUid(customerUid),
        merchantName,
        amount,
        currency,
        customData,
        noticeUrl: paymentWebhookUrl.toString(),
      })
      return success()
    } catch {
      return error()
    }
  }

  return { handlePaymentForNewCreditCard, handlePaymentForRegisteredCreditCard }
}
